
/** Blanks `STATE_CHECKING` — matches h5p-blanks `toggleButtonVisibility`. */
const H5P_BLANKS_STATE_CHECKING = 'checking';

/**
 * After restoring `previousState`, H5P.Blanks only fills inputs; it does not run the
 * Check handler (`markResults` / `showEvaluation`). Run that path so the score bar and
 * feedback match a submitted attempt. No-op for other content types.
 */
export function applyH5pBlanksSubmittedPresentation(instance: unknown): void {
    const inst = instance as {
        toggleButtonVisibility?: (state: string) => void;
        markResults?: () => void;
        showEvaluation?: () => void;
        disableInput?: () => void;
    };
    if (typeof inst.markResults !== 'function' || typeof inst.showEvaluation !== 'function') {
        return;
    }
    if (typeof inst.toggleButtonVisibility === 'function') {
        inst.toggleButtonVisibility(H5P_BLANKS_STATE_CHECKING);
    }
    inst.markResults();
    inst.showEvaluation();
    if (typeof inst.disableInput === 'function') {
        inst.disableInput();
    }
}

/**
 * After restoring `previousState`, H5P.MultiChoice applies selections but only auto-runs
 * the Check path when `behaviour.autoCheck` is true. When it is false (default), the
 * learner sees choices without correct/wrong styling or score bar. Mirror `checkAnswer`
 * feedback without firing xAPI. No-op for other content types.
 *
 * @see h5p-multi-choice `checkAnswer` / `showCheckSolution`
 */
export function applyH5pMultiChoiceSubmittedPresentation(instance: unknown): void {
    const inst = instance as {
        showCheckSolution?: (skipFeedback?: boolean) => void;
        hideButton?: (id: string) => void;
    };
    if (typeof inst.showCheckSolution !== 'function') {
        return;
    }
    inst.showCheckSolution(false);
    if (typeof inst.hideButton === 'function') {
        inst.hideButton('check-answer');
    }
}

/**
 * Apply submitted-result UI after `previousState` hydration for supported single-task
 * types (Blanks, Multi Choice). Prefer this over {@link applyH5pBlanksSubmittedPresentation}
 * alone when the library may vary.
 */
export function applyH5pHistorySubmittedPresentation(
    instance: unknown,
    options?: { hideCorrectAnswers?: boolean },
): void {
    const inst = instance as { markResults?: unknown; showEvaluation?: unknown };
    if (typeof inst.markResults === 'function' && typeof inst.showEvaluation === 'function') {
        // Blanks / AdvancedBlanks: markResults only marks the learner's own inputs
        // right/wrong + score; the solution text needs showSolutions (hidden below).
        applyH5pBlanksSubmittedPresentation(instance);
        return;
    }
    // ELMS-2370: MultiChoice `showCheckSolution` marks the learner's picks AND reveals
    // the correct options they missed in the same call — not separable, so with
    // hideCorrectAnswers we skip it (learner's selection is still restored by H5P).
    if (options?.hideCorrectAnswers) {
        return;
    }
    applyH5pMultiChoiceSubmittedPresentation(instance);
}
